import { ref, watch } from 'vue'

export interface DisplaySettings {
  showKpiArea: boolean
  showRetailer: boolean
  showDueDate: boolean
  showOverdueWarning: boolean
  showColorIndicator: boolean
  showStateBadge: boolean
}

const DEFAULT_SETTINGS: DisplaySettings = {
  showKpiArea: true,
  showRetailer: true,
  showDueDate: true,
  showOverdueWarning: true,
  showColorIndicator: true,
  showStateBadge: true,
}

const STORAGE_KEY = 'action-display-settings'

const globalSettings = ref<DisplaySettings>({ ...DEFAULT_SETTINGS })

const loadFromStorage = (): DisplaySettings => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return { ...DEFAULT_SETTINGS, ...parsed }
    }
  } catch (error) {
    console.warn('Failed to load display settings from localStorage:', error)
  }
  return { ...DEFAULT_SETTINGS }
}

const saveToStorage = (settings: DisplaySettings) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch (error) {
    console.warn('Failed to save display settings to localStorage:', error)
  }
}

globalSettings.value = loadFromStorage()

watch(
  globalSettings,
  (newSettings) => {
    saveToStorage(newSettings)
  },
  { deep: true },
)

export default function useDisplaySettings() {
  const updateSettings = (settings: DisplaySettings) => {
    globalSettings.value = { ...settings }
  }

  const resetSettings = () => {
    globalSettings.value = { ...DEFAULT_SETTINGS }
  }

  const toggleSetting = (key: keyof DisplaySettings) => {
    if (typeof globalSettings.value[key] === 'boolean') {
      globalSettings.value[key] = !globalSettings.value[key] as any
    }
  }

  return {
    settings: globalSettings,
    updateSettings,
    resetSettings,
    toggleSetting,
    DEFAULT_SETTINGS,
  }
}
